import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import "moment/locale/th";
import "./styles/ReportPage.css";
import DownloadReportWindow from "../../components/DownloadReportWindow";
import search from "/assets/icon/Search.svg";

moment.locale("th");

const formatDate = (date) => {
 const options = { day: "2-digit", month: "2-digit", year: "numeric" };
 return new Date(date).toLocaleDateString("en-GB", options);
};

const formatDateTime = (datetime) => {
 if (!datetime) return "N/A";
 const dateOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
 const timeOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
 const date = new Date(datetime);
 return `${date.toLocaleDateString("en-GB", dateOptions)} ${date.toLocaleTimeString("en-GB", timeOptions)} น.`;
};

function ShowWeatherReport() {
 const [startDate, setStartDate] = useState('');
 const [endDate, setEndDate] = useState('');
 const [searchQuery, setSearchQuery] = useState("");
 const [showDownloadReport, setShowDownloadReport] = useState(false);
 const [weatherData, setWeatherData] = useState([]);
 const [selectedItem, setSelectedItem] = useState(null);
 const [sensorData, setSensorData] = useState([]);

 const handleDateChange = () => {
   if (startDate && endDate) {
     const start = new Date(startDate);
     const end = new Date(endDate);
     
     if (start > end) {
       alert('วันที่เริ่มต้นต้องน้อยกว่าวันที่สิ้นสุด');
       return;
     }
   }
 };

 const handleSearchChange = (event) => {
   setSearchQuery(event.target.value);
 };

 const handleClearSelection = () => {
   setSearchQuery("");
   setWeatherData([]);
   setSelectedItem(null);
   setSensorData([]);
   setStartDate('');
   setEndDate('');
 };

 const handleDownloadReportClick = () => {
   setShowDownloadReport(true);
 };

 const handleDownloadReportClose = () => {
   setShowDownloadReport(false);
 };

 const fetchWeatherData = useCallback(async () => {
   if (!searchQuery) {
     setWeatherData([]);
     return;
   }
   try {
     const response = await axios.get(
       `${import.meta.env.VITE_API_URL}/api/polesSearch?searchQuery=${searchQuery}`
     );
     setWeatherData(response.data);
   } catch (error) {
     console.error("Error fetching data:", error);
   }
 }, [searchQuery]);

 const fetchSensorData = useCallback(async () => {
   if (selectedItem && startDate && endDate) {
     try {
       const response = await axios.get(
         `${import.meta.env.VITE_API_URL}/api/poles/${selectedItem.id}/sensors?startDate=${startDate}&endDate=${endDate}`
       );
       setSensorData(response.data);
     } catch (error) {
       console.error("Error fetching sensor data:", error);
     }
   } else {
     setSensorData([]);
   }
 }, [selectedItem, startDate, endDate]);

 useEffect(() => {
   fetchWeatherData();
 }, [fetchWeatherData]);

 useEffect(() => {
   fetchSensorData();
 }, [fetchSensorData]);

 const handleItemClick = (item) => {
   if (selectedItem?.id === item.id) {
     setSelectedItem(null);
     setSearchQuery("");
     setSensorData([]);
   } else {
     setSelectedItem(item);
     setSearchQuery(item.name);
   }
 };

 const createFileName = () => {
   if (!selectedItem || !startDate || !endDate) return "report";
   return `${selectedItem.name}_${startDate}_to_${endDate}`;
 };

 return (
   <div className="containerPage">
     <div className="showWeatherReport">
       <h1>รายงานสภาพอากาศ</h1>
       <h4 className="downloadReport" onClick={handleDownloadReportClick}>
         ดาวน์โหลดรายงาน
       </h4>

       <p>ดูรายงานระหว่างวันที่</p>
       <div className="sectiondatereport">
         <div className="date-inputs">
           <div className="date-input-group">
             <label>วันที่เริ่มต้น:</label>
             <input
               type="date"
               value={startDate}
               onChange={(e) => {
                 setStartDate(e.target.value);
                 handleDateChange();
               }}
               className="date-input"
             />
           </div>
           
           <div className="date-input-group">
             <label>วันที่สิ้นสุด:</label>
             <input
               type="date"
               value={endDate}
               onChange={(e) => {
                 setEndDate(e.target.value);
                 handleDateChange();
               }}
               className="date-input"
               min={startDate}
             />
           </div>
         </div>
       </div>

       <p>ค้นหารหัสเสา</p>
       <div className="searchbarreport">
         <div className="search-bar">
           <img src={search} alt="search" className="search-user" />
           <input
             type="text"
             placeholder="ค้นหารหัสเสา ชื่อเสา ตำแหน่งติดตั้ง"
             value={searchQuery}
             onChange={handleSearchChange}
           />
           {selectedItem && (
             <button className="clear-button" onClick={handleClearSelection}>
               &times;
             </button>
           )}
         </div>
       </div>

       <div className="searchResults">
         {searchQuery && (
           <>
             {selectedItem ? (
               <div
                 key={selectedItem.id}
                 className="resultItem"
                 onClick={() => handleItemClick(selectedItem)}
                 style={{ cursor: "pointer", border: "2px solid #2762F8" }}
               >
                 <p><strong>ชื่อเสา</strong> {selectedItem.name}</p>
                 <p><strong>Location</strong> {selectedItem.location}</p>
                 <p><strong>ประเภทเสา</strong> {selectedItem.pole_type}</p>
                 <p><strong>Latitude</strong> {selectedItem.lat} , {selectedItem.lng}</p>
                 <p><strong>id</strong> {selectedItem.id}</p>
               </div>
             ) : (
               weatherData.map((data) => (
                 <div
                   key={data.id}
                   className="resultItem"
                   onClick={() => handleItemClick(data)}
                   style={{ cursor: "pointer" }}
                 >
                   <p><strong>ชื่อเสา</strong> {data.name}</p>
                   <p><strong>Location</strong> {data.location}</p>
                   <p><strong>ประเภทเสา</strong> {data.pole_type}</p>
                   <p><strong>Latitude</strong> {data.lat} , {data.lng}</p>
                 </div>
               ))
             )}
           </>
         )}
       </div>
       <hr />

       {selectedItem && (
         <div className="selectedItemDetails">
           <h2>รายละเอียดเสาที่เลือก</h2>
           <p>
             <strong>ชื่อเสา:</strong>
             <span>{selectedItem.name}</span>
           </p>
           <p>
             <strong>เวลาเริ่มต้น:</strong>
             <span>{startDate || "ไม่ได้เลือก"}</span>
           </p>
           <p>
             <strong>เวลาสิ้นสุด:</strong>
             <span>{endDate || "ไม่ได้เลือก"}</span>
           </p>
         </div>
       )}

       {selectedItem && startDate && endDate && (
         sensorData.length > 0 ? (
           <div className="showReportSection">
             <table className="sensorDataTable">
               <thead>
                 <tr>
                   <th>เวลา</th>
                   <th>อุณหภูมิ</th>
                   <th>ความชื้น</th>
                   <th>ฝน</th>
                   <th>ฝนรายวัน</th>
                   <th>ความเร็วลม</th>
                   <th>ทิศทางลม</th>
                   <th>ความเร็วลมสูงสุด</th>
                   <th>ทิศทางลมสูงสุด</th>
                   <th>ความกดอากาศ</th>
                   <th>PM1</th>
                   <th>PM2.5</th>
                   <th>PM10</th>
                 </tr>
               </thead>
               <tbody>
                 {sensorData.map((data, index) => (
                   <tr key={index}>
                     <td>{formatDateTime(data.created_on)}</td>
                     <td>{data.temperature}</td>
                     <td>{data.humidity}</td>
                     <td>{data.rain}</td>
                     <td>{data.rain_daily}</td>
                     <td>{data.wind_speed}</td>
                     <td>{data.wind_dir}</td>
                     <td>{data.wind_gust_speed}</td>
                     <td>{data.wind_gust_dir}</td>
                     <td>{data.pressure}</td>
                     <td>{data.pm1}</td>
                     <td>{data.pm2_5}</td>
                     <td>{data.pm10}</td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
         ) : (
           <div className="no-data-message">
             <p>
               ไม่พบข้อมูล <span className="highlight">{selectedItem.name}</span><br />
               ระหว่างวันที่ <span className="highlight">{startDate}</span><br />
               ถึงวันที่ <span className="highlight">{endDate}</span>
             </p>
           </div>
         )
       )}

       {showDownloadReport && (
         <DownloadReportWindow
           onClose={handleDownloadReportClose}
           jsonData={{
             poleInfo: {
               name: selectedItem?.name,
               location: selectedItem?.location,
               poleType: selectedItem?.pole_type,
               latitude: selectedItem?.lat,
               longitude: selectedItem?.lng,
               id: selectedItem?.id,
             },
             reportInfo: {
               startDate: startDate,
               endDate: endDate,
             },
             sensorData: sensorData.map((data) => ({
               timestamp: formatDateTime(data.created_on),
               temperature: data.temperature,
               humidity: data.humidity,
               rain: data.rain,
               rainDaily: data.rain_daily,
               windSpeed: data.wind_speed,
               windDirection: data.wind_dir,
               windGustSpeed: data.wind_gust_speed,
               windGustDirection: data.wind_gust_dir,
               pressure: data.pressure,
               pm1: data.pm1,
               pm2_5: data.pm2_5,
               pm10: data.pm10,
             })),
           }}
           fileName={createFileName()}
           routeName="showweather"
         />
       )}
     </div>
   </div>
 );
}

export default ShowWeatherReport;